// Search API client. Wraps the SPA's shared axios instance (auth + multi-tenancy
// headers) and points at the backend module mounted at /api/v2/search/...
import { api } from '@/lib/http';
import type { Contact, Criterion, SearchResult } from './types';

// The shared axios instance already prefixes baseURL with '/api'; we mount under /v2/search.
const BASE = '/v2/search';

export interface SearchDoc {
    _id: string;
    organizationId: string;
    workspaceId?: string | null;
    createdBy: string;
    title: string;
    prompt: string;
    filters: Record<string, unknown>;
    criteria: Criterion[];
    esQuery?: unknown;
    totalHits: number;
    mode: 'live' | 'demo';
    lastRanAt?: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface ResultsWarning {
    code: 'billing_credits' | 'auth' | 'rate_limit' | 'unknown';
    message: string;
}

export interface ResultsResponse {
    mode: 'live' | 'demo';
    page: number;
    pageSize: number;
    total: number;
    esQuery: unknown;
    results: SearchResult[];
    warning?: ResultsWarning | null;
    /** True when the page was served from the Mongo cache (zero Coresignal credits spent). */
    cached?: boolean;
}

export interface HealthResponse {
    ok: boolean;
    coresignal: boolean;
    grok: boolean;
    grokModel: string;
    mode: 'live' | 'demo';
}

export interface AgentResponse {
    mode: 'live' | 'demo';
    interpretation: { filters: Record<string, unknown>; criteria: Criterion[] };
}

export type ProspectStateName = 'saved' | 'hidden' | 'viewed' | 'shortlisted' | 'contacted';

export interface ProspectStateRecord {
    prospectId: string;
    state: ProspectStateName;
    searchId: string | null;
    notes: string;
    tags: string[];
    snapshot: Contact | null;
    createdAt: string;
    updatedAt: string;
}

export async function checkHealth(): Promise<HealthResponse> {
    const { data } = await api.get<HealthResponse>(`${BASE}/health`);
    return data;
}

export async function runAgent(prompt: string): Promise<AgentResponse> {
    const { data } = await api.post<AgentResponse>(`${BASE}/agent`, { prompt });
    return data;
}

export async function createSearch(input: {
    prompt: string;
    filters: Record<string, unknown>;
    criteria: Criterion[];
    title?: string;
}): Promise<SearchDoc> {
    const { data } = await api.post<{ search: SearchDoc }>(BASE, input);
    return data.search;
}

export async function listSearches(limit = 50): Promise<SearchDoc[]> {
    const { data } = await api.get<{ searches: SearchDoc[] }>(BASE, { params: { limit } });
    return data.searches;
}

export async function getSearch(id: string): Promise<SearchDoc> {
    const { data } = await api.get<{ search: SearchDoc }>(`${BASE}/${id}`);
    return data.search;
}

export async function updateSearch(id: string, patch: Partial<{
    title: string;
    prompt: string;
    filters: Record<string, unknown>;
    criteria: Criterion[];
}>): Promise<SearchDoc> {
    const { data } = await api.patch<{ search: SearchDoc }>(`${BASE}/${id}`, patch);
    return data.search;
}

export async function deleteSearch(id: string): Promise<void> {
    await api.delete(`${BASE}/${id}`);
}

export async function runResults(
    searchId: string,
    opts: { page?: number; pageSize?: number; evaluate?: boolean; refresh?: boolean } = {},
): Promise<ResultsResponse> {
    const { data } = await api.post<ResultsResponse>(`${BASE}/${searchId}/results`, {
        page: opts.page ?? 1,
        pageSize: opts.pageSize ?? 3,
        evaluate: opts.evaluate ?? true,
        refresh: opts.refresh ?? false,
    });
    return data;
}

export async function getProspect(pid: string): Promise<Contact> {
    const { data } = await api.get<{ contact: Contact }>(`${BASE}/prospect/${encodeURIComponent(pid)}`);
    return data.contact;
}

export async function evaluateCriteria(prospects: Contact[], criteria: Criterion[]) {
    const { data } = await api.post(`${BASE}/criteria/evaluate`, { prospects, criteria });
    return data.evaluations as { id: string; verdict: 'good' | 'partial' | 'no'; reasons: any[] }[];
}

export async function setProspectState(
    searchId: string,
    prospectId: string,
    state: ProspectStateName,
    opts: { snapshot?: unknown; notes?: string; tags?: string[] } = {},
) {
    const { data } = await api.post(
        `${BASE}/${searchId}/prospects/${encodeURIComponent(prospectId)}/state`,
        { state, ...opts },
    );
    return data.prospectState;
}

export async function unsetProspectState(
    searchId: string,
    prospectId: string,
    state: ProspectStateName,
) {
    await api.delete(
        `${BASE}/${searchId}/prospects/${encodeURIComponent(prospectId)}/state/${state}`,
    );
}

export async function getProspectStates(searchId: string): Promise<Record<string, ProspectStateName[]>> {
    const { data } = await api.get<{ states: Record<string, ProspectStateName[]> }>(
        `${BASE}/${searchId}/prospects/states`,
    );
    return data.states;
}

export async function listProspectsByState(
    searchId: string,
    state: ProspectStateName,
    limit = 100,
): Promise<ProspectStateRecord[]> {
    const { data } = await api.get<{ prospects: ProspectStateRecord[] }>(
        `${BASE}/${searchId}/prospects`,
        { params: { state, limit } },
    );
    return data.prospects;
}
